import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { desktop } from '../../styles/media'

const Radio = ({ clearable, description, disabled, hidden, icon, label, labelFullWidth, name, onChange, orientation, radioId, red, rounded, value }) => {
	const checked = name === value

	const handleChange = () => {
		onChange(name)
	}

	const handleOnClear = (event) => {
		event.preventDefault()
		onChange(null)
	}

	return (
		<StyledRadio
			label={label}
			labelFullWidth={labelFullWidth}
			checked={checked}
			disabled={disabled}
			orientation={orientation}
			rounded={rounded}
			red={red}
			hidden={hidden}
			clearable={clearable}
			onClick={clearable && checked ? handleOnClear : undefined}
		>
			<Input
				type="radio"
				name={radioId}
				onChange={handleChange}
				value={name || undefined}
				checked={checked}
				label={label}
				disabled={disabled}
			/>
			{icon && icon}
			{label && (
				<Container icon={icon}>
					<Label description={description}>{label}</Label>
					{description && <Description>{description}</Description>}
				</Container>
			)}
		</StyledRadio>
	)
}

Radio.propTypes = {
	clearable: PropTypes.bool,
	description: PropTypes.string,
	disabled: PropTypes.bool,
	hidden: PropTypes.bool,
	icon: PropTypes.any,
	label: PropTypes.any,
	labelFullWidth: PropTypes.bool,
	name: PropTypes.any,
	onChange: PropTypes.func,
	orientation: PropTypes.string,
	radioId: PropTypes.string,
	red: PropTypes.bool,
	rounded: PropTypes.bool,
	value: PropTypes.any
}

Radio.defaultProps = {
	clearable: false,
	description: null,
	disabled: false,
	label: null,
	labelFullWidth: false,
	hidden: false,
	icon: null,
	name: null,
	onChange: () => null,
	orientation: 'column',
	radioId: null,
	red: false,
	rounded: false,
	value: null
}

export const StyledRadio = styled.label`
	align-items: center;
	display: inline-flex;
	margin-bottom: 0;
	width: ${props => props.labelFullWidth ? '100%' : 'fit-content'};

	${desktop`
		${props => props.rounded && !props.checked && `
			&:hover {
				border-color: ${props.theme.backgroundConcrete} !important;
				box-shadow: 0 0 0 2px ${props.theme.backgroundConcrete} !important;
			}
		`}

		${props => (!props.checked || props.clearable) && `
			cursor: pointer;
		`}
	`}

	${props => !props.label && `
		border-color: ${props.checked ? props.theme.backgroundSolid : 'transparent'};
		border-style: solid;
		border-width: 2px;
		border-radius: 25%;
		padding: 0.5rem;
		width: fit-content;

		${!props.checked && `
			&:hover {
				border-color: ${props.theme.backgroundLight};
			}
		`}
	`}

	${props => props.disabled && `
		cursor: default;

		input {
			opacity: 0.5;
		}

		svg {
			color: ${props.theme.textVeryLight};
		}
	`}

	${props => props.hidden && `
		display: none
	`}

	${props => props.rounded && `
		background: ${props.theme.background};
		border: 1px solid ${props.theme.backgroundLight};
		border-radius: 12px;
		padding: 0.75rem 1rem;
		transition: all 0.1s;

		input {
			display: none;
		}

		${props.checked ? `
			border-color: ${props.theme.primary};
			box-shadow: 0 0 0 2px ${props.theme.primary};
		` : ''}

		${props.disabled ? `
			cursor: not-allowed;
			opacity: 0.5;
			pointer-events: none;
		` : ''}
	`}

	${props => props.red && `
		background: ${props.theme.redLight};

		svg {
			color: ${props.theme.red};
		}

		${props.checked ? `
			border-color: ${props.theme.red};
			box-shadow: 0 0 0 2px ${props.theme.red};
		` : ''}
	`}
`

const Input = styled.input`
	margin-right: 0.5rem;

	${props => !props.label && `
		display: none;
	`}
`

const Container = styled.div`
	width: 100%;

	${props => props.icon && `
		margin-left: 0.5rem;
	`}
`

const Label = styled.div`
	${props => props.description && `
		font-weight: bold;
		margin-bottom: 0.25rem;
	`}
`

const Description = styled.div`
	color: ${props => props.theme.textSecondary};
`

export default Radio
